import Modal from 'react-modal';
import PropTypes from 'prop-types';
import "../../../styles/AdminPage.css";
import TextInput from '../../inputs/TextInput';
import React, { useState , useEffect  } from 'react';
import CheckboxInput from '../../inputs/CheckboxInput';
import Dropdown from '../../inputs/Dropdown';
import { createMembershipRequirement, updateMembershipRequirement } from '../../../api/admin/AdminSettingsAPI';
import NumberInput from '../../inputs/NumberInput';
import AdminSettingsEditModal from './AdminSettingsEditModal';
import InputContainer from './InputContainer';
import { getEventTypes } from '../../../api/admin/AdminDashboardAPI';
import toast from 'react-hot-toast';

Modal.setAppElement('#root');

const AdminSettingsItemModal = ({ isOpen, onClose, isAdding, item, setItems }) => {
    const [localTitle, setLocalTitle] = useState('');
    const [localDescription, setLocalDescription] = useState('');
    const [isNumber, setIsNumber] = useState(false);
    const [isPercentage, setIsPercentage] = useState(false);
    const [thresholdValue, setThresholdValue] = useState(0);
    const [eventType, setEventType] = useState(null);
    const [position, setPosition] = useState(0);

    const [eventTypes, setEventTypes] = useState([]);

    useEffect(() => {
        (async () => {
            const data = await getEventTypes();
            setEventTypes(data);
        })();
    }, []);

    useEffect(() => {
        if (!isAdding && item) {
            const requirement = item;
            setLocalTitle(requirement.title || '');
            setLocalDescription(requirement.description || '');
            setIsNumber(requirement.isNumber === 1 || requirement.isNumber === true ? true : false);
            setIsPercentage(requirement.isPercentage === 1 || requirement.isPercentage === true ? true : false);
            setThresholdValue(requirement.thresholdValue || 0);
            setPosition(requirement.position || 0);
            setEventType(requirement.eventType || requirement.eventType || null);
        } 
    }, [isAdding, item]);
    
    const modalTitle = isAdding ? "Add Requirement" : "Edit Requirement";

    const handleConfirm = async () => {
        let requirementData;

        if (isAdding) {
            requirementData = {
                title: localTitle,
                description: localDescription,
                isNumber: isNumber,
                isPercentage: isPercentage,
                thresholdValue: thresholdValue,
                eventType: eventType,
                position: position,
            };

            try {
                toast.loading("Creating Requirement...");
                const response = await createMembershipRequirement(requirementData);
                setTimeout(async () => {
                    if (response.ok) {
                        toast.dismiss();
                        try {
                            const newRequirement = await response.json();
                            toast.success("Requirement created!");
                            setItems((prev) => [...(prev ?? []), newRequirement]);
                            onClose();
                        } catch (jsonError) {
                            console.error("Error parsing JSON:", jsonError);
                            toast.error("Error parsing server response.");
                        }
                    } else {
                        toast.dismiss();
                        console.log("Error creating requirement. Status:", response.status);
                        toast.error("Requirement failed to create. Status: " + response.status);
                    }
                    
                }, 500);
            } catch (error) {
                toast.dismiss();
                console.error("Error submitting requirement:", error);
                alert("Something went wrong. Please check your connection and try again.");

            }
        } else {

            requirementData = {
                id: item.id,
                title: localTitle,
                description: localDescription,
                isNumber: isNumber,
                isPercentage: isPercentage,
                thresholdValue: thresholdValue,
                eventType: eventType,
                position: position,
            };
            try {
                toast.loading("Updaing Requirement...");
                const response = await updateMembershipRequirement(requirementData);
                setTimeout(async () => {
                        if (response.ok) {
                            toast.dismiss();
                            try {
                                toast.success("Requirement updated!");
                                setItems((prev) => (prev ?? []).map(r => r.id === item.id ? requirementData : r));
                                onClose();
                            } catch (jsonError) {
                                console.error("Error parsing JSON:", jsonError);
                                toast.error("Error parsing server response.");
                            }
                        } else {
                            toast.dismiss();
                            console.log("Error updating requirement. Status:", response.status);
                            toast.error("Semester failed to create. Status: " + response.status);
                        }
                }, 500);
            } catch (error) {
                toast.dismiss();
                console.error("Error submitting requirement:", error);
                alert("Something went wrong. Please check your connection and try again.");

            }
        }
    };

    const content = (
        <>
            <InputContainer>
                <TextInput
                    id="requirement-title"
                    placeholder="Enter Requirement Title"
                    label='Title'
                    value={localTitle}
                    setValue={(e) => setLocalTitle(e.target.value)}
                />
            </InputContainer>
            <InputContainer>
                <TextInput
                    id="requirement-description"
                    placeholder="Enter Requirement Description"
                    label='Description'
                    value={localDescription}
                    setValue={(e) => setLocalDescription(e.target.value)}
                />
            </InputContainer>
            <div className='d-flex align-items-center'>
                <InputContainer>
                    <NumberInput
                        id="requirement-threshold"
                        label='Threshold Value'
                        min={0}
                        value={thresholdValue}
                        setValue={(e) => setThresholdValue(Number(e.target.value))}
                    />
                </InputContainer>
                <CheckboxInput
                    id='AdminSettingsItemModal-numberCheckbox'
                    label='Number'
                    checked={isNumber}
                    onChange={() => {
                        setIsNumber(true); 
                        setIsPercentage(false);
                    }}
                    style={{ marginLeft: '1rem' }}
                />
                <CheckboxInput
                    id='AdminSettingsItemModal-percentageCheckbox'
                    label='Percentage'
                    checked={isPercentage}
                    onChange={() => {
                        setIsPercentage(true);
                        setIsNumber(false); 
                    }}
                    style={{ marginLeft: '1rem' }}
                />    
                
            </div>
            <InputContainer>
                <Dropdown
                    label="Event Type"
                    options={eventTypes.map(e => ({
                        value: JSON.stringify(e),   // this is stored internally
                        label: e.eventType          // this is what the user sees
                    }))}
                    onChange={(e) => {
                        const parsed = JSON.parse(e.target.value);
                        setEventType(parsed);
                    }}
                    value={JSON.stringify(eventType)}
                    style={{ maxWidth: '400px' }}
                />
            </InputContainer>
            <InputContainer>
                <NumberInput
                    id="requirement-position"
                    label='Position'
                    min={0}
                    value={position}
                    setValue={(e) => setPosition(Number(e.target.value))}
                />
            </InputContainer>
        </>
    )

    return (
        <AdminSettingsEditModal 
            modalTitle={modalTitle}
            content={content}
            isOpen={isOpen}
            onClose={onClose}
            handleConfirm={handleConfirm}
        />
    );
};

AdminSettingsItemModal.propTypes = {
    isOpen: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    isAdding: PropTypes.bool.isRequired,
    item: PropTypes.string.isRequired,
    membershipRequirements: PropTypes.array.isRequired,
    setItems: PropTypes.func.isRequired,
    eventTypes: PropTypes.array.isRequired,
};

export default AdminSettingsItemModal;
